import React, { useState, useEffect } from 'react';
import { MapPin, Mail, Send, CheckCircle } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [locations, setLocations] = useState([]);
  const [goals, setGoals] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    goal: '',
    message: ''
  });

  useEffect(() => {
    setIsVisible(true);
  }, [setIsVisible]);

  useEffect(() => {
    fetch(`${API_URL}/api/content/locations`)
      .then(res => res.json())
      .then(data => setLocations(data.locationNames || []))
      .catch(() => {});
    fetch(`${API_URL}/api/content/goals`)
      .then(res => res.json())
      .then(setGoals)
      .catch(() => {});
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send enquiry');
      }

      setIsSubmitted(true);
      toast.success(data.message || 'Your enquiry has been sent successfully!');
      
      // Reset form after success
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          phone: '',
          location: '',
          goal: '',
          message: ''
        });
        setIsSubmitted(false);
      }, 3000);
    } catch (error) {
      toast.error(error.message || 'Failed to send enquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F0EB]">
      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20">
        <div className={`container transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="max-w-3xl">
            <h1 className="heading-display mb-6">Book Your Free<br />Consultation</h1>
            <p className="body-large">
              Ready to start? Fill out the form below and I'll be in touch to arrange your 
              free consultation. No pressure, just a conversation about your goals.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="bg-white py-20 md:py-28">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Form */}
            <div>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-xs font-semibold tracking-[0.1em] uppercase text-[#1A1A1A] mb-3">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-4 border border-[#E5E0DB] bg-white focus:border-[#C9A962] focus:ring-1 focus:ring-[#C9A962] outline-none transition-colors duration-200"
                    placeholder="Your name"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="email" className="block text-xs font-semibold tracking-[0.1em] uppercase text-[#1A1A1A] mb-3">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-4 border border-[#E5E0DB] bg-white focus:border-[#C9A962] focus:ring-1 focus:ring-[#C9A962] outline-none transition-colors duration-200"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-xs font-semibold tracking-[0.1em] uppercase text-[#1A1A1A] mb-3">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-4 border border-[#E5E0DB] bg-white focus:border-[#C9A962] focus:ring-1 focus:ring-[#C9A962] outline-none transition-colors duration-200"
                      placeholder="07XXX XXX XXX"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold tracking-[0.1em] uppercase text-[#1A1A1A] mb-3">
                    Preferred Training Location *
                  </label>
                  <Select
                    value={formData.location}
                    onValueChange={(value) => handleSelectChange('location', value)}
                  >
                    <SelectTrigger className="w-full px-4 py-4 h-auto border border-[#E5E0DB] bg-white focus:border-[#C9A962] focus:ring-1 focus:ring-[#C9A962] rounded-none">
                      <SelectValue placeholder="Select a location" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map((location) => (
                        <SelectItem key={location} value={location}>
                          {location}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-xs font-semibold tracking-[0.1em] uppercase text-[#1A1A1A] mb-3">
                    Primary Goal *
                  </label>
                  <Select
                    value={formData.goal}
                    onValueChange={(value) => handleSelectChange('goal', value)}
                  >
                    <SelectTrigger className="w-full px-4 py-4 h-auto border border-[#E5E0DB] bg-white focus:border-[#C9A962] focus:ring-1 focus:ring-[#C9A962] rounded-none">
                      <SelectValue placeholder="What's your main goal?" />
                    </SelectTrigger>
                    <SelectContent>
                      {goals.map((goal) => (
                        <SelectItem key={goal} value={goal}>
                          {goal}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-xs font-semibold tracking-[0.1em] uppercase text-[#1A1A1A] mb-3">
                    Tell Me About Your Goals
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-4 py-4 border border-[#E5E0DB] bg-white focus:border-[#C9A962] focus:ring-1 focus:ring-[#C9A962] outline-none transition-colors duration-200 resize-none"
                    placeholder="Any additional details about your fitness goals, experience level, or questions..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || !formData.name || !formData.email || !formData.location || !formData.goal}
                  className="btn-gold w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-[#1A1A1A]/30 border-t-[#1A1A1A] rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : isSubmitted ? (
                    <>
                      <CheckCircle size={18} />
                      Sent Successfully!
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      Submit Enquiry
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div>
              <div className="bg-[#F5F0EB] p-8 mb-8">
                <h3 className="heading-medium mb-8">Get In Touch</h3>
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-[#C9A962] flex items-center justify-center flex-shrink-0">
                      <MapPin size={20} className="text-[#1A1A1A]" />
                    </div>
                    <div>
                      <h4 className="text-xs font-semibold tracking-[0.1em] uppercase text-[#1A1A1A] mb-1">Location</h4>
                      <p className="body-regular">East London, Leyton</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-[#C9A962] flex items-center justify-center flex-shrink-0">
                      <Mail size={20} className="text-[#1A1A1A]" />
                    </div>
                    <div>
                      <h4 className="text-xs font-semibold tracking-[0.1em] uppercase text-[#1A1A1A] mb-1">Email</h4>
                      <a href="mailto:enquiry@hefttraining.co.uk" className="body-regular text-[#C9A962] hover:underline">
                        enquiry@hefttraining.co.uk
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border border-[#E5E0DB] p-8">
                <h4 className="heading-small mb-6">What Happens Next?</h4>
                <ol className="space-y-4">
                  {[
                    "I'll review your enquiry and get back to you within 24 hours.",
                    "We'll arrange a free consultation call or in-person meeting.",
                    "If we're a good fit, we'll discuss next steps and get you started."
                  ].map((step, idx) => (
                    <li key={`step-${idx + 1}`} className="flex gap-4">
                      <span className="w-8 h-8 bg-[#1A1A1A] text-white text-sm flex items-center justify-center flex-shrink-0">
                        {idx + 1}
                      </span>
                      <p className="body-small pt-1">{step}</p>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
